/**
 * Frontend Logger for Olvidadme
 * 
 * Simple logging utility for frontend components
 * Respects environment settings and provides structured logging
 */

const isDevelopment = import.meta.env.DEV
const logLevel = import.meta.env.VITE_LOG_LEVEL || 'info'

const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3
}

const getCurrentLogLevel = () => {
  return LOG_LEVELS[logLevel] || LOG_LEVELS.info
}

/**
 * Format log message with timestamp and metadata
 */
const formatMessage = (level, message, metadata = {}) => {
  const timestamp = new Date().toISOString()
  const logEntry = {
    timestamp,
    level,
    message,
    ...metadata
  }
  
  return logEntry
}

/**
 * Check if log level is enabled
 */
const shouldLog = (level) => {
  return LOG_LEVELS[level] <= getCurrentLogLevel()
}

/**
 * Log error message
 */
const error = (message, metadata = {}) => {
  if (!shouldLog('error')) return
  
  const logEntry = formatMessage('error', message, metadata)
  
  if (isDevelopment) {
    console.error(`[ERROR] ${message}`, metadata)
  }
  
  // In production, could send to error reporting service
  return logEntry
}

/**
 * Log warning message
 */
const warn = (message, metadata = {}) => {
  if (!shouldLog('warn')) return
  
  const logEntry = formatMessage('warn', message, metadata)
  
  if (isDevelopment) {
    console.warn(`[WARN] ${message}`, metadata)
  }
  
  return logEntry
}

/**
 * Log info message
 */
const info = (message, metadata = {}) => {
  if (!shouldLog('info')) return
  
  const logEntry = formatMessage('info', message, metadata)
  
  if (isDevelopment) {
    console.info(`[INFO] ${message}`, metadata)
  }
  
  return logEntry
}

/**
 * Log debug message
 */
const debug = (message, metadata = {}) => {
  if (!shouldLog('debug')) return
  
  const logEntry = formatMessage('debug', message, metadata)
  
  if (isDevelopment) {
    console.debug(`[DEBUG] ${message}`, metadata)
  }
  
  return logEntry
}

const logger = {
  error,
  warn,
  info,
  debug
}

export default logger